import React, { useState, useMemo } from 'react';
import { Col, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserGraduate,
  faSearch,
  faCheckCircle,
  faTimesCircle,
  faAward,
  faExchangeAlt,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import './commission.css';

export default function List({ commission }) {
  const [studentSearch, setStudentSearch] = useState('');

  const stats = useMemo(() => {
    if (!commission || !commission.students || commission.students.length < 1) {
      return { total: 0, up: 0, down: 0, scholarship: 0, commissionChanged: 0 };
    }

    let counter = { up: 0, down: 0, total: 0, scholarship: 0, commissionChanged: 0 };

    commission.students.forEach((student) => {
      counter.total++;

      if (student.isActive) {
        const classroomIndex = Array.isArray(student.classroom)
          ? student.classroom.indexOf(commission.classroom)
          : -1;

        const query = typeof student.isActive === 'object' && classroomIndex !== -1
          ? student.isActive[classroomIndex]
          : student.isActive;

        if (
          student.scholarship &&
          classroomIndex !== -1 &&
          student.scholarship[classroomIndex]
        ) {
          counter.scholarship++;
        }

        switch (query) {
          case 'up':
            counter.up++;
            break;
          case 'down':
            counter.down++;
            break;
          case 'commissionChanged':
            counter.commissionChanged++;
            break;
          default:
            break;
        }
      }
    });

    return counter;
  }, [commission]);

  const filteredStudents = useMemo(() => {
    if (!commission || !commission.students) return [];
    if (!studentSearch.trim()) return commission.students;

    const term = studentSearch.toLowerCase().trim();
    return commission.students.filter((student) => {
      const fullName = `${student.name || ''} ${student.surname || ''}`.toLowerCase();
      const email = (student.email || '').toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
  }, [commission, studentSearch]);

  const getStudentStatusDetails = (student) => {
    const classroomIndex = Array.isArray(student.classroom)
      ? student.classroom.indexOf(commission.classroom)
      : -1;

    try {
      if (
        student.scholarship &&
        classroomIndex !== -1 &&
        student.scholarship[classroomIndex]
      ) {
        return {
          cardClass: 'status-scholarship',
          tagClass: 'student-tag-scholarship',
          label: 'Beca',
        };
      }

      let activeVal = typeof student.isActive === 'object' && classroomIndex !== -1
        ? student.isActive[classroomIndex]
        : student.isActive;

      if (activeVal === 'down') {
        return {
          cardClass: 'status-down',
          tagClass: 'student-tag-down',
          label: 'Baja',
        };
      }
      if (activeVal === 'done') {
        return {
          cardClass: 'status-done',
          tagClass: 'student-tag-done',
          label: 'Finalizado',
        };
      }
      if (activeVal === 'commissionChanged') {
        return {
          cardClass: 'status-changed',
          tagClass: 'student-tag-changed',
          label: 'Cambio Comisión',
        };
      }
      if (activeVal === 'up') {
        return {
          cardClass: 'status-default',
          tagClass: 'student-tag-active',
          label: 'Activo',
        };
      }
    } catch {
      // Fallback
    }

    return {
      cardClass: 'status-default',
      tagClass: 'student-tag-active',
      label: student.isActive ? 'Activo' : 'Sin Estado',
    };
  };

  const getInitials = (name, surname) => {
    const n = (name && name[0]) || '';
    const s = (surname && surname[0]) || '';
    return `${n}${s}`.toUpperCase() || '?';
  };

  return (
    <Col xs={12} lg={5} className='mb-4'>
      <div className='commission-card p-4 h-100 d-flex flex-column'>
        {/* Card Header */}
        <div className='d-flex align-items-center justify-content-between pb-3 border-bottom mb-3'>
          <div className='d-flex align-items-center'>
            <div
              className='mr-2 rounded-circle d-flex align-items-center justify-content-center'
              style={{
                width: 36,
                height: 36,
                backgroundColor: '#eff6ff',
                color: '#2563eb',
              }}
            >
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div>
              <h5 className='mb-0 font-weight-bold text-dark'>Alumnos</h5>
              <small className='text-muted'>Listado de inscriptos</small>
            </div>
          </div>
          {commission && commission.students && (
            <span className='badge badge-primary badge-pill px-3 py-2' style={{ fontSize: '0.85rem' }}>
              {stats.total} {stats.total === 1 ? 'Alumno' : 'Alumnos'}
            </span>
          )}
        </div>

        {/* Quick Stats Badges */}
        {commission && commission.students && commission.students.length > 0 && (
          <div className='d-flex flex-wrap align-items-center mb-3'>
            <div className='stat-pill stat-pill-active'>
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>Activos: {stats.up}</span>
            </div>
            <div className='stat-pill stat-pill-danger'>
              <FontAwesomeIcon icon={faTimesCircle} />
              <span>Bajas: {stats.down}</span>
            </div>
            <div className='stat-pill stat-pill-scholarship'>
              <FontAwesomeIcon icon={faAward} />
              <span>Becas: {stats.scholarship}</span>
            </div>
            <div className='stat-pill stat-pill-warning'>
              <FontAwesomeIcon icon={faExchangeAlt} />
              <span>Cambios: {stats.commissionChanged}</span>
            </div>
          </div>
        )}

        {/* Filter Input inside student list */}
        {commission && commission.students && commission.students.length > 0 && (
          <div className='mb-3'>
            <InputGroup size='sm'>
              <InputGroup.Prepend>
                <InputGroup.Text className='bg-white border-right-0 text-muted'>
                  <FontAwesomeIcon icon={faSearch} size='sm' />
                </InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                type='text'
                size='sm'
                className='border-left-0 commission-input'
                placeholder='Filtrar alumno por nombre...'
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
            </InputGroup>
          </div>
        )}

        {/* Student List Items */}
        <div className='student-list-container flex-grow-1'>
          {!commission || !commission.students ? (
            <div className='text-center py-5 text-muted d-flex flex-column align-items-center justify-content-center h-100'>
              <FontAwesomeIcon icon={faUserGraduate} size='3x' className='mb-3 text-secondary opacity-50' />
              <p className='lead mb-1'>Sin comisión seleccionada</p>
              <small>Seleccione una comisión en la barra superior para ver sus alumnos</small>
            </div>
          ) : commission.students.length === 0 ? (
            <div className='text-center py-5 text-muted d-flex flex-column align-items-center justify-content-center h-100'>
              <FontAwesomeIcon icon={faUsers} size='3x' className='mb-3 text-secondary opacity-50' />
              <p className='lead mb-1'>No hay alumnos asignados</p>
              <small>Esta comisión aún no cuenta con estudiantes inscriptos</small>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className='text-center py-4 text-muted'>
              <p className='mb-0'>No se encontraron alumnos con "{studentSearch}"</p>
            </div>
          ) : (
            filteredStudents.map((student, index) => {
              const statusDetails = getStudentStatusDetails(student);
              const initials = getInitials(student.name, student.surname);

              return (
                <div
                  key={index}
                  className={`student-card-item ${statusDetails.cardClass}`}
                >
                  <div className='d-flex align-items-center' style={{ minWidth: 0 }}>
                    <div className='student-avatar mr-2'>
                      {initials}
                    </div>
                    <div className='text-truncate'>
                      <div className='font-weight-bold text-dark text-truncate' style={{ fontSize: '0.9rem' }}>
                        {student.name} {student.surname}
                      </div>
                      {student.email && (
                        <small className='text-muted text-truncate d-block' style={{ fontSize: '0.78rem' }}>
                          {student.email}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className='ml-2 flex-shrink-0'>
                    <span className={`student-tag ${statusDetails.tagClass}`}>
                      {statusDetails.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Col>
  );
}
